"use client"
import Link from "next/link"
import { useEffect, useState } from "react"
import Icon from "@/components/ui/Icon"
import { PLAN_CONFIG, QUARTERLY_DISCOUNT } from "@/lib/payments/plans"
import { jsonLdScript } from "@/lib/jsonld"

const plans = [
  {
    id: "fsbo",
    key: "FSBO" as const,
    name: "FSBO (Landlord)",
    sub: "For owners selling or renting direct",
    price: PLAN_CONFIG.FSBO.listingFee!,
    yearlyPrice: PLAN_CONFIG.FSBO.listingFee!,
    period: "per listing",
    desc: "Pay once. Listing runs 60 days.",
    features: PLAN_CONFIG.FSBO.features,
    missing: ["No featured boost"],
    cta: "Start listing",
    href: "/list-property",
    popular: false,
  },
  {
    id: "agent-starter",
    key: "AGENT_SOLO" as const,
    name: "Agent Starter",
    sub: "For independent agents",
    price: PLAN_CONFIG.AGENT_SOLO.monthlyPrice!,
    yearlyPrice: PLAN_CONFIG.AGENT_SOLO.yearlyPrice!,
    period: "/month",
    desc: `Up to ${PLAN_CONFIG.AGENT_SOLO.listingLimit} active listings`,
    features: PLAN_CONFIG.AGENT_SOLO.features,
    missing: [],
    cta: "Get started",
    href: "/auth/register",
    popular: false,
  },
  {
    id: "agent-pro",
    key: "AGENT_PRO" as const,
    name: "Agent Pro",
    sub: "For agents growing their book",
    price: PLAN_CONFIG.AGENT_PRO.monthlyPrice!,
    yearlyPrice: PLAN_CONFIG.AGENT_PRO.yearlyPrice!,
    period: "/month",
    desc: `Up to ${PLAN_CONFIG.AGENT_PRO.listingLimit} active listings`,
    features: PLAN_CONFIG.AGENT_PRO.features,
    missing: [],
    cta: "Get started",
    href: "/auth/register",
    popular: false,
  },
  {
    id: "agency-50",
    key: "AGENCY" as const,
    name: "Agency 50",
    sub: "For estate agencies with a team",
    price: PLAN_CONFIG.AGENCY.monthlyPrice!,
    yearlyPrice: PLAN_CONFIG.AGENCY.yearlyPrice!,
    period: "/month",
    desc: `${PLAN_CONFIG.AGENCY.listingLimit} shared listings · ${PLAN_CONFIG.AGENCY.agentSeats} agent seats`,
    features: PLAN_CONFIG.AGENCY.features,
    missing: [],
    cta: "Get started",
    href: "/auth/register",
    popular: true,
  },
  {
    id: "agency-100",
    key: "AGENCY_100" as const,
    name: "Agency 100",
    sub: "For larger agencies",
    price: PLAN_CONFIG.AGENCY_100.monthlyPrice!,
    yearlyPrice: PLAN_CONFIG.AGENCY_100.yearlyPrice!,
    period: "/month",
    desc: `${PLAN_CONFIG.AGENCY_100.listingLimit} shared listings · ${PLAN_CONFIG.AGENCY_100.agentSeats} agent seats`,
    features: PLAN_CONFIG.AGENCY_100.features,
    missing: [],
    cta: "Get started",
    href: "/auth/register",
    popular: false,
  },
  {
    id: "market-leader",
    key: "MARKET_LEADER" as const,
    name: "Market Leader",
    sub: "For the agencies setting the pace",
    price: PLAN_CONFIG.MARKET_LEADER.monthlyPrice!,
    yearlyPrice: PLAN_CONFIG.MARKET_LEADER.yearlyPrice!,
    period: "/month",
    desc: `${PLAN_CONFIG.MARKET_LEADER.listingLimit} shared listings · ${PLAN_CONFIG.MARKET_LEADER.agentSeats} agent seats`,
    features: PLAN_CONFIG.MARKET_LEADER.features,
    missing: [],
    cta: "Get started",
    href: "/auth/register",
    popular: false,
  },
  {
    id: "developer",
    key: "DEVELOPER" as const,
    name: "Developer",
    sub: "For property developers",
    price: PLAN_CONFIG.DEVELOPER.monthlyPrice!,
    yearlyPrice: PLAN_CONFIG.DEVELOPER.yearlyPrice!,
    period: "/project/mo",
    desc: "Per active development · $50 each at 3+",
    features: PLAN_CONFIG.DEVELOPER.features,
    missing: [],
    cta: "Contact sales",
    href: "/contact",
    popular: false,
  },
]

// FAQ copy adapts to the live paywall state — first answer differs while the
// free trial is active vs when plans are purchasable.
function buildFaqs(trialActive: boolean) {
  return [
    {
      q: "Do I need a credit card to start?",
      a: trialActive
        ? "No. Everything on Zim Estates is free right now. No card required."
        : "No card is needed to browse or create an account. You only pay when you subscribe to a plan or publish a paid listing — via EcoCash, card, or bank transfer.",
    },
    { q: "Can I pay to rank higher in search?", a: "No — and nobody else can either. Organic search order is based on verification, trust and relevance only. Paid promotions (featured, spotlight) appear in clearly labelled sections so buyers always know what is an ad." },
    { q: "What is the difference between FSBO (Landlord) and Agent Starter?", a: "FSBO (Landlord) is for property owners selling or renting out their own home. Agent Starter is for licensed estate agents managing multiple client properties." },
    { q: "Can I upgrade or downgrade my plan?", a: "Yes. You can change your plan at any time from your dashboard. Changes take effect on your next billing cycle." },
    { q: "How does KYC verification work?", a: "You upload your National ID and a selfie. Our AI does an initial check, then a human reviewer approves within 24 hours. This is required before any listing goes live." },
    { q: "Do you take commission on sales?", a: "No. Zim Estates is a listing platform. We connect buyers and sellers directly. We never handle money or take commission on transactions." },
  ]
}

type PlanPrices = { monthlyPrice: number | null; yearlyPrice: number | null; listingFee: number | null; listingLimit?: number; agentSeats?: number }
type Pricing = Record<string, PlanPrices> & {
  featuredBoostPrice: number
  featuredBoost30dPrice?: number
  spotlightPrice?: number
  buyerBlastPrice?: number
  extraSeatPrice?: number
  extraListingsBlockPrice?: number
}
type Paywall = { isTrialActive: boolean; trialExpiry: string | null; paywallActive: boolean }

type BillingPeriod = "monthly" | "quarterly" | "yearly"

export default function PricingPage() {
  const [period, setPeriod] = useState<BillingPeriod>("monthly")
  const [paywall, setPaywall] = useState<Paywall | null>(null)
  const [pricing, setPricing] = useState<Pricing | null>(null)

  useEffect(() => {
    fetch("/api/site-config")
      .then(r => r.json())
      .then(d => { if (d?.paywall) setPaywall(d.paywall); if (d?.pricing) setPricing(d.pricing) })
      .catch(() => {})
  }, [])

  // Default to trial-active while loading so prices never flash before we know the
  // live state (matches the server's fail-safe: unknown → treat as free trial).
  const trialActive = paywall ? paywall.isTrialActive : true

  const FAQS = buildFaqs(trialActive)
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map(f => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  }

  return (
    <div style={{ background: "var(--cream)", minHeight: "100vh" }}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLdScript(faqJsonLd) }} />
      <div className="section">

        {/* HEADER */}
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <span className="eyebrow">Pricing</span>
          <h1 style={{ fontFamily: "Fraunces, serif", fontSize: 48, fontWeight: 700, color: "var(--navy)", margin: "20px 0 12px", lineHeight: 1.1 }}>
            {trialActive
              ? <>Start free. <em style={{ color: "var(--gold-deep)", fontStyle: "italic" }}>No card needed.</em></>
              : <>Simple plans. <em style={{ color: "var(--gold-deep)", fontStyle: "italic" }}>Honest pricing.</em></>}
          </h1>
          <p style={{ color: "var(--muted)", fontSize: 16, maxWidth: 560, margin: "0 auto 32px" }}>
            {trialActive
              ? <>Every plan and every feature is free right now. Pick your role and everything unlocks instantly.</>
              : <>Transparent pricing for every type of seller. Choose the plan that fits how you sell.</>}
          </p>

          {/* Billing period toggle — only relevant once real prices are shown.
              Quarterly was implemented at checkout but never surfaced here, so a
              browsing customer had no way to learn it exists before starting
              checkout — this toggle is the fix. */}
          {!trialActive && (
            <div style={{ display: "inline-flex", background: "var(--cream-warm)", border: "1px solid var(--line)", borderRadius: 10, padding: 4, gap: 4, flexWrap: "wrap" }}>
              {([
                { key: "monthly", label: "Monthly" },
                { key: "quarterly", label: `Quarterly · Save ${Math.round(QUARTERLY_DISCOUNT * 100)}%` },
                { key: "yearly", label: "Yearly · Save 20%" },
              ] as const).map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setPeriod(opt.key)}
                  style={{ padding: "8px 20px", borderRadius: 8, border: "none", fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", background: period === opt.key ? "var(--cream)" : "transparent", color: "var(--navy)", boxShadow: period === opt.key ? "var(--shadow)" : "none" }}
                >{opt.label}</button>
              ))}
            </div>
          )}
        </div>

        {/* LAUNCH OFFER — only while the free trial is active */}
        {trialActive && (
          <div className="reveal-scale" style={{ background: "var(--cream-warm)", border: "1px solid var(--line)", borderRadius: 16, padding: "24px 32px", textAlign: "center", marginBottom: 48 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--gold-deep)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Free right now</div>
            <div style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 600, color: "var(--navy)", marginBottom: 6 }}>
              Everything is free
            </div>
            <div style={{ fontSize: 14, color: "var(--muted)" }}>All plans, all features, all listings. No credit card required to start.</div>
          </div>
        )}

        {/* PLANS */}
        <div className="mob-grid-4" style={{ gap: 20, marginBottom: 64 }}>
          {plans.map((plan, i) => {
            const pd = pricing?.[plan.key]
            const monthlyEff = plan.id === "fsbo" ? (pd?.listingFee ?? plan.price) : (pd?.monthlyPrice ?? plan.price)
            const yearlyEff = plan.id === "fsbo" ? (pd?.listingFee ?? plan.yearlyPrice) : (pd?.yearlyPrice ?? plan.yearlyPrice)
            // Quarterly isn't a stored price — it's monthlyEff discounted by the
            // same QUARTERLY_DISCOUNT the server applies at checkout (3-11mo band).
            const quarterlyEff = parseFloat((monthlyEff * (1 - QUARTERLY_DISCOUNT)).toFixed(2))
            const shown = plan.id === "fsbo" ? monthlyEff
              : period === "yearly" ? yearlyEff
              : period === "quarterly" ? quarterlyEff
              : monthlyEff
            const cta = trialActive ? "Get started free" : plan.cta
            const href = trialActive ? "/auth/register" : plan.href
            return (
            <div key={plan.id} className="reveal-scale" style={{ background: plan.popular ? "var(--navy)" : "var(--cream)", border: `1px solid ${plan.popular ? "transparent" : "var(--line)"}`, borderRadius: 20, padding: 28, position: "relative", boxShadow: plan.popular ? "var(--shadow-lg)" : "none", transitionDelay: `${i * 80}ms` }}>
              {plan.popular && (
                <div style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", background: "var(--gold)", color: "var(--navy)", fontSize: 11, fontWeight: 700, padding: "4px 14px", borderRadius: 20, textTransform: "uppercase", whiteSpace: "nowrap" }}>
                  Most Popular
                </div>
              )}

              <div style={{ marginBottom: 20 }}>
                <div style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 600, color: plan.popular ? "var(--cream)" : "var(--navy)", marginBottom: 4 }}>{plan.name}</div>
                <div style={{ fontSize: 13, color: plan.popular ? "rgba(253,251,247,0.7)" : "var(--muted)", marginBottom: 16 }}>{plan.sub}</div>
                {trialActive ? (
                  <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                    <span style={{ fontFamily: "Fraunces, serif", fontSize: 40, fontWeight: 700, color: plan.popular ? "var(--cream)" : "var(--navy)" }}>Free</span>
                    <span style={{ fontSize: 14, color: plan.popular ? "rgba(253,251,247,0.6)" : "var(--muted)" }}>right now</span>
                  </div>
                ) : (
                  <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                    <span style={{ fontFamily: "Fraunces, serif", fontSize: 40, fontWeight: 700, color: plan.popular ? "var(--cream)" : "var(--navy)" }}>
                      ${shown}
                    </span>
                    <span style={{ fontSize: 14, color: plan.popular ? "rgba(253,251,247,0.6)" : "var(--muted)" }}>{plan.period}</span>
                  </div>
                )}
                {/* plan.desc is static copy and the Developer one carries a price
                    ("$50 each at 3+"), which must not show under "Free". */}
                <div style={{ fontSize: 13, color: plan.popular ? "rgba(253,251,247,0.6)" : "var(--muted)", marginTop: 4 }}>
                  {trialActive && plan.key === "DEVELOPER" ? "Per active development" : plan.desc}
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 24 }}>
                {plan.features.map(f => {
                  const isSoon = f.includes("(coming soon)")
                  const label = f.replace(" (coming soon)", "")
                  return (
                    <div key={f} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: isSoon ? (plan.popular ? "rgba(253,251,247,0.45)" : "var(--muted)") : (plan.popular ? "rgba(253,251,247,0.85)" : "var(--ink)") }}>
                      <span style={{ color: isSoon ? (plan.popular ? "rgba(253,251,247,0.3)" : "var(--line)") : (plan.popular ? "var(--gold)" : "var(--green)"), fontWeight: 700, flexShrink: 0 }}>{isSoon ? "◷" : "✓"}</span>
                      {label}{isSoon && <span style={{ fontSize: 10, fontWeight: 700, background: plan.popular ? "rgba(253,251,247,0.1)" : "rgba(27,45,84,0.06)", color: plan.popular ? "rgba(253,251,247,0.5)" : "var(--muted)", borderRadius: 4, padding: "1px 5px", marginLeft: 2 }}>SOON</span>}
                    </div>
                  )
                })}
                {plan.missing.map(f => (
                  <div key={f} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: plan.popular ? "rgba(253,251,247,0.4)" : "var(--muted)" }}>
                    <span style={{ flexShrink: 0 }}>·</span>
                    {f}
                  </div>
                ))}
              </div>

              <Link href={href} style={{ display: "block", textAlign: "center", padding: "13px", background: plan.popular ? "var(--gold)" : "transparent", color: plan.popular ? "var(--navy)" : "var(--navy)", border: plan.popular ? "none" : "1px solid var(--line)", borderRadius: 10, fontSize: 14, fontWeight: 700 }}>
                {cta}
              </Link>
            </div>
          )})}
        </div>

        {/* THE PLEDGE — the one thing money cannot buy here */}
        <div className="reveal" style={{ background: "var(--navy)", borderRadius: 20, padding: "32px 36px", textAlign: "center", marginBottom: 64 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--gold)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Our ranking pledge</div>
          <div style={{ fontFamily: "Fraunces, serif", fontSize: 26, fontWeight: 700, color: "var(--cream)", marginBottom: 10 }}>
            You cannot buy your way to the top of Zim Estates.
          </div>
          <p style={{ fontSize: 14, color: "rgba(253,251,247,0.75)", maxWidth: 640, margin: "0 auto", lineHeight: 1.6 }}>
            Search results are ordered by verification, trust and relevance — never by who paid more.
            Featured listings and spotlights are clearly labelled promotions in their own sections,
            so buyers always know the difference between an ad and an honest result.
          </p>
        </div>

        {/* ADD-ONS */}
        <div className="mob-grid-3" style={{ gap: 20, marginBottom: 64 }}>
          {[
            { title: "Featured boost", desc: "Pin your listing to the top of the featured rail. 7 or 30 days.", price: trialActive ? "Free right now" : `$${pricing?.featuredBoostPrice ?? 15}/7d · $${pricing?.featuredBoost30dPrice ?? 25}/30d`, icon: "*", href: null },
            { title: "Homepage spotlight", desc: "Your listing on the Zim Estates homepage for 24 hours. Only 4 slots at a time, clearly labelled Sponsored.", price: trialActive ? "Free right now" : `$${pricing?.spotlightPrice ?? 10}/24h`, icon: "*", href: null },
            { title: "Matched-buyer alert", desc: "We email every buyer whose saved search matches your listing. Targeted — never spray.", price: trialActive ? "Free right now" : `$${pricing?.buyerBlastPrice ?? 40}/send`, icon: "Message", href: null },
            {
              title: "Extra seats & listings",
              desc: trialActive
                ? "Grow without changing plans: extra agency seats and +10 listing blocks."
                : `Grow without changing plans: extra agency seats $${pricing?.extraSeatPrice ?? 5}/mo, +10 listings $${pricing?.extraListingsBlockPrice ?? 10}/mo.`,
              price: trialActive ? "Free right now" : `From $${pricing?.extraSeatPrice ?? 5}/mo`,
              icon: "*",
              href: null,
            },
            { title: "Price drop alerts", desc: "Set a target price on any listing and we email you the moment the asking price falls to it. Free for all buyers.", price: "Free always", icon: "Message", href: null },
            { title: "Photography", desc: "Professional property photography in Harare, Bulawayo, Gweru. Optional add-on.", price: "From $40/shoot", icon: "Photo", href: "https://wa.me/263781270233?text=Hi%2C%20I'm%20interested%20in%20property%20photography%20for%20my%20listing%20on%20Zim%20Estates." },
          ].map((addon, i) => (
            <div key={addon.title} className="reveal" style={{ background: "var(--cream-warm)", border: "1px solid var(--line)", borderRadius: 16, padding: 24, transitionDelay: `${i * 80}ms`, position: "relative" }}>
              <div style={{ marginBottom: 12 }}>{addon.icon === "*" ? <span style={{ fontSize: 28 }}>*</span> : <Icon name={addon.icon} size={28} />}</div>
              <div style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 600, color: "var(--navy)", marginBottom: 6 }}>{addon.title}</div>
              <div style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.5, marginBottom: 16 }}>{addon.desc}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 600, color: "var(--gold-deep)" }}>{addon.price}</div>
                {addon.href && (
                  <a href={addon.href} target="_blank" rel="noopener noreferrer" style={{ padding: "6px 14px", background: "#075E54", color: "white", borderRadius: 8, fontSize: 12, fontWeight: 700, textDecoration: "none" }}>
                    Book via WhatsApp
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* FAQ */}
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 32, fontWeight: 600, color: "var(--navy)", textAlign: "center", marginBottom: 40 }}>Common questions</h2>
          {FAQS.map((faq, i) => (
            <div key={faq.q} className="reveal" style={{ borderBottom: "1px solid var(--line)", paddingBottom: 20, marginBottom: 20, transitionDelay: `${i * 60}ms` }}>
              <div style={{ fontWeight: 600, color: "var(--navy)", marginBottom: 8, fontSize: 15 }}>{faq.q}</div>
              <div style={{ fontSize: 14, color: "var(--muted)", lineHeight: 1.6 }}>{faq.a}</div>
            </div>
          ))}
        </div>

      </div>
    </div>
  )
}
